/**
 * ContextMenu — small popup menu positioned at click coordinates.
 *
 * Props:
 *   x, y      number   — viewport coordinates of the click
 *   items     Array<{ label, icon?, onClick, disabled?, divider?, danger? }>
 *             A swatch-row item: { swatches: [{value,label}], current, onPick }
 *             renders an inline row of color buttons (e.g. tag a DB dev/prod).
 *
 * `danger: true` styles the item with the warning/danger colour — use for
 * destructive actions (drop / truncate / delete).
 *   onClose   fn()
 *
 * Each item's onClick fires, then the menu closes automatically.
 * Items with `divider: true` render as a horizontal rule (label optional).
 * Items with `disabled: true` are greyed out and unclickable.
 *
 * The menu auto-closes on outside click and Escape.
 */
import { For, Show } from 'solid-js';
import { useDismissable } from '../lib/dismissable.js';

export default function ContextMenu(props) {
  let containerRef;
  useDismissable(() => containerRef, () => props.onClose?.());

  // Clamp to viewport so the menu never opens off-screen.
  const style = () => {
    const menuW = 220;
    const menuH = (props.items?.length ?? 0) * 30 + 8;
    const left = Math.min(props.x ?? 0, window.innerWidth  - menuW - 8);
    const top  = Math.min(props.y ?? 0, window.innerHeight - menuH - 8);
    return {
      position: 'fixed',
      top:      `${Math.max(8, top)}px`,
      left:     `${Math.max(8, left)}px`,
      'min-width': `${menuW}px`,
      'z-index': 1100,
    };
  };

  return (
    <div ref={containerRef} class="context-menu" style={style()}>
      <For each={props.items}>
        {(item) => (
          <Show when={!item.swatches} fallback={
            <div class="context-menu-swatches">
              <For each={item.swatches}>
                {(sw) => (
                  <button
                    type="button"
                    class="cm-swatch"
                    classList={{ selected: (item.current || '') === sw.value, none: sw.value === '' }}
                    style={sw.value ? `background:${sw.value};` : ''}
                    title={sw.label}
                    aria-label={sw.label}
                    onClick={() => { item.onPick?.(sw.value); props.onClose?.(); }}
                  >{sw.value === '' ? '∅' : ((item.current || '') === sw.value ? '✓' : '')}</button>
                )}
              </For>
            </div>
          }>
          <Show
            when={!item.divider}
            fallback={<div class="context-menu-divider" />}
          >
            <button
              class={`context-menu-item${item.danger ? ' context-menu-item-danger' : ''}`}
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                item.onClick?.();
                props.onClose?.();
              }}
            >
              <Show when={item.icon}>
                <span class="context-menu-icon">{item.icon}</span>
              </Show>
              <span class="context-menu-label">{item.label}</span>
            </button>
          </Show>
          </Show>
        )}
      </For>
    </div>
  );
}
